import React, { useState, useEffect } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { ShieldCheck, UserPlus, Trash2, Loader2, AlertCircle, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';


const roleBadge = {
  super_admin: 'bg-amber-500/15 text-amber-400',
  admin: 'bg-indigo-500/15 text-indigo-400',
  viewer: 'bg-slate-500/15 text-slate-400',
};

const roleDesc = {
  super_admin: 'Full access: dashboard, users, provisioning, admin management',
  admin: 'Dashboard, user management, provisioning',
  viewer: 'Dashboard view only',
};

export default function AdminAdminsPage() {
  const { admin: currentAdmin } = useAdmin();
  const { isDark, colors } = useAdminTheme();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);

  const fetchAdmins = async () => {
    try {
      const res = await axios.get(`${API}/admin/admins`, { withCredentials: true });
      setAdmins(res.data.admins);
    } catch {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAdmins(); }, []);

  const handleRemove = async (adminId, name) => {
    if (!window.confirm(`Remove admin ${name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/admins/${adminId}`, { withCredentials: true });
      toast.success(`${name} removed`);
      setAdmins((prev) => prev.filter((a) => a.admin_id !== adminId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const handleRoleChange = async (adminId, newRole) => {
    try {
      await axios.put(`${API}/admin/admins/${adminId}/role?role=${newRole}`, {}, { withCredentials: true });
      toast.success('Role updated');
      setAdmins((prev) => prev.map((a) => a.admin_id === adminId ? { ...a, role: newRole } : a));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update role');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="admin-admins-page">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold" style={{ color: colors.text }}>Admin Management</h1>
          <Button
            onClick={() => setShowInvite(true)}
            className="rounded-xl text-white gap-2 text-sm"
            style={{ background: colors.accent }}
            data-testid="invite-admin-btn"
          >
            <UserPlus className="h-4 w-4" /> Add Admin
          </Button>
        </div>

        {/* Role legend */}
        <div className="flex flex-wrap gap-4 text-xs" style={{ color: colors.textMuted }}>
          {Object.entries(roleDesc).map(([role, desc]) => (
            <div key={role} className="flex items-center gap-1.5">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${roleBadge[role]}`}>{role.replace('_', ' ')}</span>
              <span>{desc}</span>
            </div>
          ))}
        </div>

        {/* Admin list */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin" style={{ color: colors.textMuted }} /></div>
          ) : admins.length === 0 ? (
            <p className="text-center py-12" style={{ color: colors.textMuted }}>No admins found</p>
          ) : (
            admins.map((a) => {
              const isSelf = a.admin_id === currentAdmin?.admin_id;
              return (
                <div
                  key={a.admin_id}
                  className="flex items-center justify-between p-4 rounded-2xl"
                  style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }}
                  data-testid={`admin-row-${a.admin_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: isDark ? 'rgba(51,65,85,0.5)' : 'rgba(241,245,249,1)' }}>
                      <ShieldCheck className="h-4 w-4" style={{ color: colors.textSecondary }} />
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: colors.text }}>
                        {a.name} {isSelf && <span className="text-xs" style={{ color: colors.textMuted }}>(you)</span>}
                      </p>
                      <p className="text-xs" style={{ color: colors.textMuted }}>{a.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {isSelf ? (
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase ${roleBadge[a.role]}`}>
                        {a.role.replace('_', ' ')}
                      </span>
                    ) : (
                      <>
                        <select
                          value={a.role}
                          onChange={(e) => handleRoleChange(a.admin_id, e.target.value)}
                          className="rounded-lg px-2 py-1 text-xs focus:outline-none"
                          style={{ background: colors.input, border: `1px solid ${colors.inputBorder}`, color: colors.text }}
                          data-testid={`role-select-${a.admin_id}`}
                        >
                          <option value="super_admin">Super Admin</option>
                          <option value="admin">Admin</option>
                          <option value="viewer">Viewer</option>
                        </select>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemove(a.admin_id, a.name)}
                          className="h-7 w-7 p-0 text-slate-500 hover:text-red-400 hover:bg-red-500/10"
                          data-testid={`remove-admin-${a.admin_id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onSuccess={() => { setShowInvite(false); fetchAdmins(); }}
        />
      )}
    </AdminLayout>
  );
}

function InviteModal({ onClose, onSuccess }) {
  const { isDark, colors } = useAdminTheme();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('viewer');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleInvite = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/admins/invite`, { email, name, role }, { withCredentials: true });
      setResult(res.data);
      toast.success('Admin invited');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invite failed');
    } finally {
      setLoading(false);
    }
  };

  const copyPassword = async () => {
    await navigator.clipboard.writeText(result.temporary_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const inputStyle = { background: colors.input, border: `1px solid ${colors.inputBorder}`, color: colors.text };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="invite-modal">
      <div className="w-full max-w-sm rounded-2xl p-6" style={{ background: isDark ? '#1e293b' : '#ffffff', border: `1px solid ${colors.cardBorder}` }}>
        {result ? (
          <div className="space-y-4">
            <h3 className="text-base font-semibold" style={{ color: colors.text }}>Admin Invited</h3>
            <p className="text-sm" style={{ color: colors.textSecondary }}>Share these credentials with <span style={{ color: colors.text }}>{result.name}</span>:</p>
            <div className="rounded-xl p-4 space-y-2" style={{ background: colors.input, border: `1px solid ${colors.inputBorder}` }}>
              <p className="text-xs" style={{ color: colors.textMuted }}>Email</p>
              <p className="text-sm font-mono" style={{ color: colors.text }}>{result.email}</p>
              <p className="text-xs mt-3" style={{ color: colors.textMuted }}>Temporary Password</p>
              <div className="flex items-center gap-2">
                <p className="text-sm font-mono flex-1" style={{ color: colors.text }}>{result.temporary_password}</p>
                <Button variant="ghost" size="sm" onClick={copyPassword} className="h-7 w-7 p-0" style={{ color: colors.textSecondary }} data-testid="copy-temp-password">
                  {copied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-amber-400/80 bg-amber-400/10 rounded-xl px-3 py-2">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              They should change this password on first login.
            </div>
            <Button onClick={onSuccess} className="w-full rounded-xl text-white" style={{ background: colors.accent }}>Done</Button>
          </div>
        ) : (
          <form onSubmit={handleInvite} className="space-y-4">
            <h3 className="text-base font-semibold" style={{ color: colors.text }}>Add Admin</h3>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-xl px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none" style={inputStyle} placeholder="Admin name" required data-testid="invite-name" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-xl px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none" style={inputStyle} placeholder="email@example.com" required data-testid="invite-email" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Role</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full rounded-xl px-4 py-2.5 text-sm focus:outline-none" style={inputStyle} data-testid="invite-role">
                <option value="super_admin">Super Admin — Full access</option>
                <option value="admin">Admin — Users & provisioning</option>
                <option value="viewer">Viewer — Dashboard only</option>
              </select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" onClick={onClose} variant="ghost" className="flex-1 rounded-xl" style={{ color: colors.textSecondary }}>Cancel</Button>
              <Button type="submit" disabled={loading} className="flex-1 rounded-xl text-white gap-2" style={{ background: colors.accent }} data-testid="invite-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                Invite
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
